import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import weakKey from 'weak-key';

import { BasketActionsType, BasketItemType } from '../../../types/basket';
import * as Api from '../../../weborder/Api';
import * as BasketControl from '../../../weborder/BasketControl';
import { Button, ShoppingCartCounter, Typography } from '../../atoms';
import { InfoFilled as InfoFilledIcon } from '../../icons';
import { BasketItem } from '../../molecules';
import { basketUtils } from './utils/basketUtils';

export interface BasketProps {
  actions: BasketActionsType;
  basketItems: BasketControl.Item[];
  buttonLabel: string;
  currency: Api.Currency;
  languageCode: string;
  isCheckoutEnabled: boolean;
  menuId: number;
  minOrder: number;
  translate: any;
  vrId: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  basket: {
    backgroundColor: theme.palette.common.white,
    bottom: 0,
    left: 0,
    position: 'fixed',
    width: '100%',
    zIndex: 10,

    '& $basketItems': {
      display: 'none',
    },

    '& $header': {
      display: 'none',
    },
  },

  header: {
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    display: 'flex',
    flexFlow: 'row nowrap',
    margin: theme.spacing(0, 3),
    padding: theme.spacing(3, 0, 2.5),

    '& .shoppingCart': {
      marginRight: theme.spacing(2.5),
    },
  },

  basketItems: {
    margin: theme.spacing(0, 3),
  },

  checkoutButton: {
    '& .buttonState': {
      alignItems: 'center',
      display: 'flex',
      flexFlow: 'row nowrap',
      width: '100%',
    },

    '& [class*="MuiButton-contained"]': {
      padding: theme.spacing(1.5, 2),
    },
  },
  buttonContent: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    width: '100%',
  },
  minOrderWarning: {
    alignItems: 'center',
    color: theme.palette.grey[800],
    display: 'flex',
    flexFlow: 'row nowrap',

    '& svg': {
      marginRight: theme.spacing(1),
      color: [theme.palette.info.main, '!important'],
    },
  },

  [theme.breakpoints.up('sm')]: {
    basket: {
      left: 'auto',
      right: theme.spacing(1),
      width: '378px',
    },
    checkoutButton: {
      borderTop: 'none',
      padding: 0,

      '& [class*="MuiButton-contained"]': {
        padding: theme.spacing(2.5, 3),
      },
    },
  },

  [theme.breakpoints.up('lg')]: {
    basket: {
      position: 'static',
      transform: 'none',
      width: '100%',

      '& $basketItems': {
        display: 'block',
      },

      '& $header': {
        display: 'flex',
        flexFlow: 'row nowrap',
      },
    },

    container: {
      border: `1px solid ${theme.palette.grey[400]}`,
      borderBottom: `none`,
    },

    basketItems: {
      paddingLeft: 0,
      paddingBottom: theme.spacing(1.5),
    },
  },
}));

export const Basket = (props: BasketProps): JSX.Element => {
  const classes = useStyles();
  const {
    actions,
    basketItems,
    buttonLabel,
    currency,
    languageCode,
    isCheckoutEnabled,
    menuId,
    minOrder,
    translate,
    vrId,
  } = props;

  const basketTotal = BasketControl.Total(basketItems);
  const basketItemsSummary = basketUtils.summariseBasketItems(basketItems);
  const isOrderSufficient = basketTotal >= minOrder;
  const localisedBasketTotal = BasketControl.ToCurrency(
    basketTotal,
    currency,
    languageCode,
  );
  const localisedMinOrder = BasketControl.ToCurrency(
    minOrder,
    currency,
    languageCode,
  );

  const renderCheckoutButton = () => {
    let buttonContent = null;

    if (isOrderSufficient) {
      buttonContent = (
        <>
          <Typography variant="h3">
            <strong>{buttonLabel}</strong>
          </Typography>
        </>
      );
    } else {
      buttonContent = (
        <div className={classes.minOrderWarning}>
          <InfoFilledIcon size="xs" />
          <Typography variant="body2">
            {translate('Menu_Basket_MinimumOrder')} {localisedMinOrder}
          </Typography>
        </div>
      );
    }

    const isEnabled = isOrderSufficient && isCheckoutEnabled;

    return (
      <Button disabled={!isEnabled} fullWidth onClick={actions.onCheckout}>
        <div className={classes.buttonContent}>
          {buttonContent}

          <Typography variant="h3">
            <strong>{localisedBasketTotal}</strong>
          </Typography>
        </div>
      </Button>
    );
  };

  const basketItemCount = Object.values(basketItemsSummary).reduce(
    (acc, basketItem) => (acc += basketItem.quantity),
    0,
  );

  return (
    <div className={classes.basket} data-fd="menu-basket">
      <div className={classes.container}>
        <div className={classes.header}>
          <ShoppingCartCounter
            className="shoppingCart"
            count={basketItemCount}
            size="s"
          />
          <Typography variant="h3">{translate('Basket')}</Typography>
        </div>

        <ul className={classes.basketItems}>
          {Object.values(basketItemsSummary).map(
            (basketItem: BasketItemType) => (
              <BasketItem
                actions={actions}
                currency={currency}
                languageCode={languageCode}
                basketItem={basketItem}
                key={weakKey({ basketItem })}
                menuId={menuId}
                translate={translate}
                vrId={vrId}
              />
            ),
          )}
        </ul>
      </div>

      <div className={classes.checkoutButton}>{renderCheckoutButton()}</div>
    </div>
  );
};
